import useSWRInfinite, {
  SWRInfiniteConfiguration,
  SWRInfiniteResponse
} from "swr/infinite";
import { swrFetcher } from "rivals/services/utils";
import type { RequestParams, Response } from "./types";

export const BASE_URL_USERS_ALERTS = "/api/v2/users/alerts";

// getFetcher, by wrapping swrFetcher, provides a means for injecting the startDate query param without affecting the
// cache key. See why this is necessary in comment within getKey. As an alternative approach, I tried deleting old cache
// keys when appropriate, but couldn't find a good way to identify which keys should be deleted. This might be the
// cleanest approach anyway, especially when there are more query params to consider.
//
// The purpose of the start date is so that, while scrolling through pages, if new page items are created, the page
// offset doesn't become stale. Alternatively, the already traversed pages could be continuously revalidated, and that
// is the default behavior of the useSWRInfinite hook. The problem with that approach is heaviness: each scroll end
// induced page fetch requires re-fetching all previously traversed pages. So the start date solves the problem of stale
// page offsets without killing performance.
function getFetcher(startDateSeconds: number | null) {
  return function (url: string): Promise<Response> {
    return swrFetcher<Response>(
      `${url}&start_date_seconds=${startDateSeconds}`
    );
  };
}

const getKey = (page: number, previousPageData: Response): string | null => {
  if (previousPageData && !previousPageData.userAlerts.length) {
    // returning null tells useSWRInfinite that we've reached the end of pages
    return null;
  }

  // Its crucial that the start date for the API request is not included in the key. If included, the key of the first
  // page would change over time, resulting in pages being associated with multiple keys, which ultimately results in
  // the useSWRInfinite hook wanting to make an API request for each of the keys for a given page when the cache is
  // invalidated.
  return `${BASE_URL_USERS_ALERTS}?page=${page}`;
};

export default function useGetUserAlerts(
  { startDateSeconds }: RequestParams,
  config?: SWRInfiniteConfiguration
): SWRInfiniteResponse<Response> {
  return useSWRInfinite<Response>(getKey, getFetcher(startDateSeconds), config);
}
